import { useState, useEffect } from "react";
import * as signalR from "@microsoft/signalr";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { Badge, Avatar, notification } from "antd";
import { observer } from "mobx-react-lite";
import ApiUrls from "../../constants/ApiUrls";
import { AlertIco, CloseMediumIco } from "../../assets/customIcons";
import styles from "./BellNotification.module.scss";
import Constants from "../../constants/Common";
import { ITooltip, Spinner } from "../../ui-kit";
import { authStore } from "../../store/authStore";
import { delay } from "lodash";

const BellNotification = observer(() => {
  const {
    titleIcons: { alert },
  } = Constants;
  const [connection, setConnection] = useState(null);
  const [bell, setBell] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isPlay, setIsPlay] = useState(false);
  const [newEventCount, setNewEventCount] = useState(0);
  const [updatedEventCount, setUpdatedEventCount] = useState(0);
  const [isShow, setIsShow] = useState(false);

  const emptyEventsCount = () => {
    return { newEventCount: 0, updatedEventCount: 0 };
  };

  useEffect(() => {
    const bellNotificationStr = localStorage.getItem("bellNotification");
    const bellNotification = bellNotificationStr
      ? JSON.parse(bellNotificationStr)
      : emptyEventsCount();
    setNewEventCount(bellNotification.newEventCount);
    setUpdatedEventCount(bellNotification.updatedEventCount);
    setBell(
      (prev) =>
        (prev ?? 0) +
        bellNotification.newEventCount +
        bellNotification.updatedEventCount,
    );
  }, []);

  const resetNotification = () => {
    localStorage.setItem(
      "bellNotification",
      JSON.stringify(emptyEventsCount()),
    );
    setNewEventCount(0);
    setUpdatedEventCount(0);
    setBell(null);
    setIsOpen(true);
  };

  type NotificationType = "success" | "info" | "warning" | "error";

  const openNotification = (
    type: NotificationType,
    message,
    description,
    duration = 6,
  ) => {
    notification[type]({
      message,
      description,
      top: 100,
      maxCount: 2,
      duration,
      closeIcon: <CloseMediumIco />,
      className: "defect-notice",
    });
  };

  const openAllNotifications = () => {
    const args = {
      message: "Уведомление о дефектах",
      description: `Добавлено дефектов: ${newEventCount}
        Обновлено дефектов: ${updatedEventCount}`,
      duration: 6,
      closeIcon: <CloseMediumIco />,
      top: 55,
    };
    notification.open(args);
  };

  useEffect(() => {
    let dispConnection;
    const token = localStorage.getItem("token");
    if (token) {
      dispConnection = new HubConnectionBuilder()
        .withUrl(`${ApiUrls.webApiUrl}/signalrHubs/dispatcherHub`, {
          withCredentials: false,
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build();
      //newConnection.serverTimeoutInMilliseconds = 240000;
      setConnection(dispConnection);
    }

    return () => {
      // Возвращаем функцию в useEffect, чтобы при отключение компонента
      // отключить WebSocket
      if (dispConnection) {
        dispConnection?.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (
      connection &&
      connection?.state === signalR.HubConnectionState.Disconnected
    ) {
      connection
        .start()
        .then(() => {
          setIsShow(true);
          connection.on("ReceiveBellNotification", (message) => {
            if (authStore.defectNotify) {
              console.log("Received notifications: " + message.number);
            }
          });

          connection.on(
            "ReceiveNewAndUpdatedDefectsNotification",
            (message) => {
              setIsPlay(false);
              if (authStore.defectNotify) {
                let description = null;
                if (message.newEvents?.length) {
                  message.newEventsCount = message.newEvents.length;
                  const desc = message.newEvents.map(
                    (item) =>
                      `№ дефекта: ${item.key}\nВремя отключения: ${item.timeDisable}\nОрганизация: ${item.organization}`,
                  );
                  description = desc.join("\n\n");
                  openNotification(
                    "success",
                    "Добавление дефектов",
                    description,
                    20,
                  );
                }
                if (message.newEventsCount) {
                  description = `Добавлено ${message.newEventsCount} новых дефектов`;
                  openNotification(
                    "success",
                    "Добавление дефектов",
                    description,
                    20,
                  );
                }
                if (description) {
                }
                description = null;
                if (message.updatedEvents?.length) {
                  message.updatedEventsCount = message.updatedEvents.length;
                  const desc = message.updatedEvents.map(
                    (item) =>
                      `№ дефекта: ${item.key}\nВремя отключения: ${item.timeDisable}\nОрганизация: ${item.organization}`,
                  );
                  description = desc.join("\n\n");
                  openNotification(
                    "success",
                    "Обновление дефектов",
                    description,
                    20,
                  );
                }
                if (message.updatedEventsCount) {
                  description = `Статус обновлен у ${message.updatedEventsCount} дефектов`;
                  openNotification(
                    "success",
                    "Обновление статуса",
                    description,
                    20,
                  );
                }
                const eventCnt = newEventCount + message.newEventsCount;
                const updatedEventCnt =
                  updatedEventCount + message.updatedEventsCount;
                setNewEventCount((prev) => prev + message.newEventsCount);
                setUpdatedEventCount(
                  (prev) => prev + message.updatedEventsCount,
                );

                localStorage.setItem(
                  "bellNotification",
                  JSON.stringify({
                    newEventCount: eventCnt,
                    updatedEventCount: updatedEventCnt,
                  }),
                );

                setBell(
                  (prev) =>
                    (prev || 0) +
                    message.newEventsCount +
                    message.updatedEventsCount,
                );
                setIsPlay(true);
              }
            },
          );
        })
        .catch((e) => {
          setConnection(null);
          setIsShow(false);
          console.log("Connection failed: ", e);
        });
    }
  }, [connection]);

  return (
    <>
      {isShow && (
        <>
          <ITooltip title={alert} placement="right">
            <Badge
              className={styles.notification}
              count={bell}
              overflowCount={999}
            >
              <Avatar
                shape="circle"
                size="large"
                icon={<AlertIco />}
                onClick={() => {
                  if (isOpen) {
                    openAllNotifications();
                    setIsOpen((prev) => !prev);
                    delay(resetNotification, 5000);
                  }
                }}
              />
            </Badge>
          </ITooltip>
          <iframe
            src="/audio/silence.mp3"
            allow="autoplay"
            style={{ display: "none" }}
          ></iframe>
          {isPlay && (
            <audio id="audio" autoPlay>
              <source src="/audio/notificationAudio.mp3" type="audio/mp3" />
            </audio>
          )}
        </>
      )}
    </>
  );
});

export default BellNotification;
